import { productOptionService } from "@/services/ProductOptionService";
import { toast } from "vue3-toastify";
import { i18n } from "@/lang";

const state = {
    optionLists: [],
    isAddingOptionList: false,
    getOptionListsRequest: {
        loading: false,
    },
    saveOptionListRequest: {
        loading: false,
    },
    saveOptionItemRequest: {
        loading: false,
    },
    duplicateOptionListRequest: {
        loading: false,
    },
    deleteOptionListsRequest: {
        loading: false,
    },
};

export function optionListDisplayName(optionList) {
    const localized = optionList?.nameLocalized;
    if (!localized || typeof localized !== "object") return "";
    const locale = typeof i18n.global.locale === "string"
        ? i18n.global.locale
        : i18n.global.locale.value;
    return localized[locale] || localized.en || localized.fr || localized.nl || "";
}

function unwrap(response) {
    return response?.data !== undefined && response?.data !== null ? response.data : response;
}

function toastError(error, fallbackKey) {
    if (typeof error === "string" && error) {
        toast.error(error);
    } else {
        toast.error(i18n.global.t(fallbackKey));
    }
}

function sortItems(items) {
    return [...(items || [])].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
}

const getters = {
    getAllOptionLists(state) {
        const lists = Array.isArray(state.optionLists) ? state.optionLists : [];
        return lists.map((list) => ({ ...list, displayName: optionListDisplayName(list) }));
    },
};

const mutations = {
    setGetOptionListsRequest(state, { loading, message }) {
        state.getOptionListsRequest = { loading, message };
    },
    setSaveOptionListRequest(state, { loading, message }) {
        state.saveOptionListRequest = { loading, message };
    },
    setSaveOptionItemRequest(state, { loading, message }) {
        state.saveOptionItemRequest = { loading, message };
    },
    setDuplicateOptionListRequest(state, { loading, message }) {
        state.duplicateOptionListRequest = { loading, message };
    },
    setDeleteOptionListsRequest(state, { loading, message }) {
        state.deleteOptionListsRequest = { loading, message };
    },
    setIsAddingOptionList(state, value) {
        state.isAddingOptionList = value;
    },
    setOptionLists(state, optionLists) {
        state.optionLists = Array.isArray(optionLists) ? optionLists : [];
    },
    addOptionListToList(state, optionList) {
        state.optionLists = [...state.optionLists, optionList];
    },
    replaceOptionListInList(state, optionList) {
        state.optionLists = state.optionLists.map((element) =>
            element.optionListId === optionList.optionListId
                ? { ...optionList, items: element.items ?? [] }
                : element
        );
    },
    removeOptionListFromList(state, optionListId) {
        state.optionLists = state.optionLists.filter(
            (element) => element.optionListId !== optionListId
        );
    },
    removeOptionListsFromList(state, optionListIds) {
        const removed = new Set(optionListIds);
        state.optionLists = state.optionLists.filter(
            (element) => !removed.has(element.optionListId)
        );
    },
    setOptionListItems(state, { optionListId, items }) {
        state.optionLists = state.optionLists.map((list) =>
            list.optionListId === optionListId
                ? { ...list, items: sortItems(items || []) }
                : list
        );
    },
};

const actions = {
    fetchOptionLists({ commit }) {
        commit("setGetOptionListsRequest", { loading: true });
        return productOptionService
            .getOptionLists()
            .then((response) => {
                commit("setGetOptionListsRequest", { loading: false });
                commit("setOptionLists", unwrap(response));
            })
            .catch((error) => {
                commit("setGetOptionListsRequest", { loading: false, message: error });
                toastError(error, "toast.errorEdit");
            });
    },

    async addOptionList({ commit }, optionList) {
        commit("setSaveOptionListRequest", { loading: true });
        try {
            const response = await productOptionService.addOptionList(optionList);
            const created = unwrap(response);
            commit("addOptionListToList", { ...created, items: created.items || [] });
            commit("setSaveOptionListRequest", { loading: false });
            toast.success(i18n.global.t("toast.successEdit"));
            return created;
        } catch (error) {
            commit("setSaveOptionListRequest", { loading: false, message: error });
            toastError(error, "toast.errorEdit");
            return null;
        }
    },

    async updateOptionList({ commit }, { optionListId, optionList, silent }) {
        commit("setSaveOptionListRequest", { loading: true });
        try {
            const response = await productOptionService.updateOptionList(optionListId, optionList);
            commit("replaceOptionListInList", unwrap(response));
            commit("setSaveOptionListRequest", { loading: false });
            if (!silent) {
                toast.success(i18n.global.t("toast.successEdit"));
            }
            return true;
        } catch (error) {
            commit("setSaveOptionListRequest", { loading: false, message: error });
            toastError(error, "toast.errorEdit");
            return false;
        }
    },

    async deleteOptionList({ commit }, optionListId) {
        try {
            await productOptionService.deleteOptionList(optionListId);
            commit("removeOptionListFromList", optionListId);
            commit("product/removeOptionListFromProducts", optionListId, { root: true });
            toast.success(i18n.global.t("toast.deleteSuccess"));
            return true;
        } catch (error) {
            toastError(error, "toast.deleteError");
            return false;
        }
    },

    async deleteOptionLists({ commit }, optionListIds) {
        commit("setDeleteOptionListsRequest", { loading: true });
        try {
            await productOptionService.deleteOptionLists(optionListIds);
            commit("removeOptionListsFromList", optionListIds);
            optionListIds.forEach((optionListId) =>
                commit("product/removeOptionListFromProducts", optionListId, { root: true })
            );
            commit("setDeleteOptionListsRequest", { loading: false });
            toast.success(i18n.global.t("toast.deleteSuccess"));
            return true;
        } catch (error) {
            commit("setDeleteOptionListsRequest", { loading: false, message: error });
            toastError(error, "toast.deleteError");
            return false;
        }
    },

    async duplicateOptionList({ commit }, { optionList, withItems }) {
        commit("setDuplicateOptionListRequest", { loading: true });
        try {
            const nameLocalized = Object.fromEntries(
                Object.entries(optionList.nameLocalized || {})
                    .filter(([, name]) => name)
                    .map(([locale, name]) => [
                        locale,
                        i18n.global.t("productOptionsPage.copyName", locale, { name }),
                    ])
            );
            const created = unwrap(await productOptionService.addOptionList({
                nameLocalized,
                minChoiceTotal: optionList.minChoiceTotal ?? null,
                maxChoiceTotal: optionList.maxChoiceTotal ?? null,
                forceChoiceTotal: optionList.forceChoiceTotal ?? null,
            }));
            let items = [];
            if (withItems && optionList.items?.length) {
                const optionItems = sortItems(optionList.items).map((item) => ({
                    optionListId: created.optionListId,
                    productId: item.productId,
                    free: item.free,
                    priceOverride: item.priceOverride,
                    type: item.type,
                    allowMultipleChoice: item.allowMultipleChoice,
                    maxChoice: item.maxChoice,
                    includeOptionLists: !!item.includeOptionLists,
                }));
                items = sortItems(unwrap(await productOptionService.saveOptionItems(created.optionListId, optionItems)));
            }
            const duplicated = { ...created, items };
            commit("addOptionListToList", duplicated);
            commit("setDuplicateOptionListRequest", { loading: false });
            toast.success(i18n.global.t("toast.duplicateSuccess"));
            return duplicated;
        } catch (error) {
            commit("setDuplicateOptionListRequest", { loading: false, message: error });
            toastError(error, "toast.errorEdit");
            return null;
        }
    },

    async saveOptionItems({ commit }, { optionListId, optionItems, silent }) {
        commit("setSaveOptionItemRequest", { loading: true });
        try {
            const response = await productOptionService.saveOptionItems(optionListId, optionItems);
            commit("setOptionListItems", { optionListId, items: unwrap(response) });
            commit("setSaveOptionItemRequest", { loading: false });
            if (!silent) {
                toast.success(i18n.global.t("toast.successEdit"));
            }
            return true;
        } catch (error) {
            commit("setSaveOptionItemRequest", { loading: false, message: error });
            toastError(error, "toast.errorEdit");
            return false;
        }
    },
};

export const productOption = {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
